// SPDX-License-Identifier: AGPL-3.0-or-later

import {BoostGemIcon} from '@app/features/boost/components/BoostGemIcon';
import {GuildBoostLevelBadge} from '@app/features/boost/components/GuildBoostLevelBadge';
import styles from '@app/features/boost/components/GuildBoostLevelTrack.module.css';
import {PERK_DETAILS} from '@app/features/boost/components/GuildBoostPerks';
import type {GuildBoostLevelCard, GuildBoostStatus} from '@app/features/boost/utils/GuildBoostLevels';
import {
	ACTIVE_DESCRIPTOR,
	BOOST_COUNT_DESCRIPTOR,
	BOOSTS_TO_GO_DESCRIPTOR,
	guildBoostPerkTitle,
	LEVEL_DESCRIPTOR,
	LOCKED_DESCRIPTOR,
	NEW_PERK_DESCRIPTOR,
	NO_LEVEL_DESCRIPTOR,
	PROGRESS_DESCRIPTOR,
	REMAINING_BOOSTS_DESCRIPTOR,
	TOP_LEVEL_DESCRIPTOR,
} from '@app/features/boost/utils/GuildBoostMessages';
import {useLingui} from '@lingui/react/macro';
import {CheckCircleIcon, LockSimpleIcon} from '@phosphor-icons/react';
import type React from 'react';

type CardState = 'active' | 'next' | 'locked';

function cardState(level: number, status: GuildBoostStatus): CardState {
	if (level <= status.level) return 'active';
	return status.next?.level === level ? 'next' : 'locked';
}

function GuildBoostProgress({status, levels}: {status: GuildBoostStatus; levels: ReadonlyArray<GuildBoostLevelCard>}) {
	const {i18n} = useLingui();
	const top = levels[levels.length - 1].boosts;
	const goal = status.next?.boosts ?? top;
	return (
		<div
			className={styles.progress}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={goal}
			aria-valuenow={Math.min(status.count, goal)}
			aria-label={i18n._(PROGRESS_DESCRIPTOR, {count: status.count, goal})}
			style={{'--fill': Math.min(1, status.count / top)} as React.CSSProperties}
			data-flx="boost.level-track.progress"
		>
			<div className={styles.progressBar} />
			{levels.map((level) => (
				<span
					key={level.level}
					className={styles.tick}
					data-reached={status.level >= level.level}
					style={{'--at': level.boosts / top} as React.CSSProperties}
				>
					<GuildBoostLevelBadge level={level.level} size={16} />
					<span>{level.boosts}</span>
				</span>
			))}
		</div>
	);
}

export function GuildBoostStatusHero({
	status,
	levels,
	children,
}: {
	status: GuildBoostStatus;
	levels: ReadonlyArray<GuildBoostLevelCard>;
	children?: React.ReactNode;
}) {
	const {i18n} = useLingui();
	return (
		<section className={styles.hero} data-flx="boost.level-track.hero">
			<div className={styles.heroTop}>
				<span className={styles.heroBadge}>
					{status.level > 0 ? <GuildBoostLevelBadge level={status.level} size={24} /> : <BoostGemIcon size={24} />}
				</span>
				<div className={styles.heroText}>
					<h2 className={styles.heroTitle}>
						{status.level > 0 ? i18n._(LEVEL_DESCRIPTOR, {level: status.level}) : i18n._(NO_LEVEL_DESCRIPTOR)}
					</h2>
					<span className={styles.heroSubtitle}>
						{status.next
							? i18n._(REMAINING_BOOSTS_DESCRIPTOR, {
									count: Math.max(0, status.next.boosts - status.count),
									level: status.next.level,
								})
							: i18n._(TOP_LEVEL_DESCRIPTOR)}
					</span>
				</div>
				<span className={styles.heroCount}>
					<BoostGemIcon size={20} />
					<strong>{status.count}</strong>
					{status.next && <span>/ {status.next.boosts}</span>}
				</span>
			</div>
			<GuildBoostProgress status={status} levels={levels} />
			{children && <div className={styles.heroActions}>{children}</div>}
		</section>
	);
}

export function GuildBoostLevelTrack({
	levels,
	status,
}: {
	levels: ReadonlyArray<GuildBoostLevelCard>;
	status: GuildBoostStatus;
}) {
	const {i18n} = useLingui();
	return (
		<ol className={styles.track} data-flx="boost.level-track">
			{levels.map((level, index) => {
				const state = cardState(level.level, status);
				const segment =
					level.level === status.level && status.next
						? Math.min(1, Math.max(0, (status.count - level.boosts) / (status.next.boosts - level.boosts)))
						: 1;
				return (
					<li
						key={level.level}
						className={styles.card}
						data-state={state}
						data-current={level.level === status.level}
						style={{'--i': index, '--seg': segment} as React.CSSProperties}
						data-flx="boost.level-track.card"
					>
						<div className={styles.rail} data-reached={status.level >= level.level} aria-hidden="true">
							<span className={styles.node}>
								<GuildBoostLevelBadge level={level.level} size={24} />
							</span>
						</div>
						<div className={styles.body}>
							<header className={styles.cardHeader}>
								<div className={styles.cardHeading}>
									<h3 className={styles.cardTitle}>{i18n._(LEVEL_DESCRIPTOR, {level: level.level})}</h3>
									<span className={styles.cardSubtitle}>
										<BoostGemIcon size={16} />
										{i18n._(BOOST_COUNT_DESCRIPTOR, {count: level.boosts})}
									</span>
								</div>
								{state === 'active' ? (
									<span className={styles.state} data-state="active" data-flx="boost.level-track.state">
										<CheckCircleIcon size={16} weight="fill" aria-hidden="true" />
										{i18n._(ACTIVE_DESCRIPTOR)}
									</span>
								) : state === 'next' ? (
									<span className={styles.state} data-state="next" data-flx="boost.level-track.state">
										{i18n._(BOOSTS_TO_GO_DESCRIPTOR, {count: Math.max(0, level.boosts - status.count)})}
									</span>
								) : (
									<span className={styles.state} data-state="locked" data-flx="boost.level-track.state">
										<LockSimpleIcon size={14} weight="bold" aria-hidden="true" />
										{i18n._(LOCKED_DESCRIPTOR)}
									</span>
								)}
							</header>
							<ul className={styles.perks}>
								{level.perks.map((perk) => {
									const {Icon, description} = PERK_DETAILS[perk.type];
									return (
										<li
											key={perk.type}
											className={styles.perk}
											data-upgraded={perk.upgraded}
											data-perk={perk.type}
											data-flx="boost.level-track.perk"
										>
											<Icon size={20} aria-hidden="true" className={styles.perkIcon} />
											<div className={styles.perkText}>
												<span className={styles.perkTitle}>
													{guildBoostPerkTitle(i18n, perk)}
													{perk.upgraded && <span className={styles.newChip}>{i18n._(NEW_PERK_DESCRIPTOR)}</span>}
												</span>
												<span className={styles.perkDescription}>{i18n._(description)}</span>
											</div>
										</li>
									);
								})}
							</ul>
						</div>
					</li>
				);
			})}
		</ol>
	);
}
